import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useAuthStore from '../stores/authStore';
import { Eye, EyeOff, ShieldCheck } from 'lucide-react';

const FirstLoginOTP = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { verifyFirstLogin, loading, error, clearError } = useAuthStore();

  // identifier passed via navigation state from Login
  const identifier = location.state?.identifier || '';

  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [localError, setLocalError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    clearError();
    if (!identifier) {
      // If no identifier was passed, redirect back to login
      navigate('/');
    }
  }, [identifier, navigate, clearError]);

  const passwordRules = [
    { label: 'At least 8 characters', test: (p) => p.length >= 8 },
    { label: 'One uppercase letter (A–Z)', test: (p) => /[A-Z]/.test(p) },
    { label: 'One number (0–9)', test: (p) => /\d/.test(p) },
    { label: 'One special character (!@#$…)', test: (p) => /[!@#$%^&*(),.?":{}|<>]/.test(p) },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');
    clearError();

    if (otp.length !== 6 || !/^\d{6}$/.test(otp)) {
      setLocalError('Please enter a valid 6-digit OTP.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setLocalError('Passwords do not match.');
      return;
    }
    const failedRule = passwordRules.find((r) => !r.test(newPassword));
    if (failedRule) {
      setLocalError(`Password must have: ${failedRule.label.toLowerCase()}.`);
      return;
    }

    const result = await verifyFirstLogin(identifier, otp, newPassword);
    if (result) {
      setSuccess(true);
      setTimeout(() => navigate('/dashboard'), 1500);
    }
  };

  const displayError = localError || error;

  return (
    <div className="login-container">
      <div className="login-card" style={{ maxWidth: 460 }}>
        {/* Header */}
        <div className="login-header">
          <div className="login-logo">
            <img src="/tickmarks-logo.png" alt="Tickmarks Logo" style={{ height: '40px', marginBottom: '12px' }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '8px' }}>
            <ShieldCheck size={36} color="var(--primary)" />
          </div>
          <div className="login-subtitle">Set Your New Password</div>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '6px', textAlign: 'center' }}>
            A 6-digit OTP has been sent to your email. Enter it below along with your new password.
          </p>
        </div>

        {/* Success banner */}
        {success && (
          <div className="login-error" style={{ background: 'rgba(16,185,129,0.1)', borderColor: '#10b981', color: '#065f46', marginBottom: '16px' }}>
            ✓ Password set successfully! Redirecting…
          </div>
        )}

        {/* Error banner */}
        {displayError && !success && (
          <div className="login-error" style={{ marginBottom: '16px' }}>
            {displayError}
          </div>
        )}

        <form className="login-form" onSubmit={handleSubmit}>
          {/* OTP */}
          <div className="form-group">
            <label className="form-label">6-Digit OTP</label>
            <input
              type="text"
              className="form-input"
              placeholder="Enter OTP from your email"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              maxLength={6}
              inputMode="numeric"
              required
              style={{ letterSpacing: '6px', fontSize: '20px', textAlign: 'center' }}
            />
          </div>

          {/* New Password */}
          <div className="form-group">
            <label className="form-label">New Password</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                className="form-input"
                placeholder="Create a strong password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                style={{ paddingRight: '48px' }}
              />
              <button
                type="button"
                style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)' }}
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            {/* Password strength checklist */}
            {newPassword.length > 0 && (
              <ul style={{ listStyle: 'none', padding: '8px 0 0 0', margin: 0 }}>
                {passwordRules.map((r) => (
                  <li key={r.label} style={{ fontSize: '12px', color: r.test(newPassword) ? '#10b981' : 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '2px' }}>
                    <span>{r.test(newPassword) ? '✓' : '○'}</span>
                    {r.label}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Confirm Password */}
          <div className="form-group">
            <label className="form-label">Confirm Password</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showConfirm ? 'text' : 'password'}
                className="form-input"
                placeholder="Re-enter your password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                style={{ paddingRight: '48px' }}
              />
              <button
                type="button"
                style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)' }}
                onClick={() => setShowConfirm(!showConfirm)}
              >
                {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {confirmPassword.length > 0 && newPassword !== confirmPassword && (
              <p style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px' }}>Passwords do not match.</p>
            )}
          </div>

          <button
            type="submit"
            className="btn btn-primary btn-block"
            disabled={loading || success}
            style={{ width: '100%', marginTop: '10px' }}
          >
            {loading ? 'Verifying…' : 'Set Password & Log In'}
          </button>
        </form>

        <div style={{ marginTop: '16px', textAlign: 'center' }}>
          <button
            type="button"
            style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontSize: '13px' }}
            onClick={() => navigate('/')}
          >
            ← Back to Login
          </button>
        </div>
      </div>
    </div>
  );
};

export default FirstLoginOTP;
